import { FC, useMemo } from "react"
import { Field } from "formik"
import Form from "../../../../form"
import CurrencyInput from "../../../../form/currency-input"
import DreamRateField from "../../../../dream-rate/form/fields/dream-rate"
import { useFetchConformingLimitQuery } from "../../../../../../redux/queries"
import {
  LOAN_TYPE_OPTIONS,
  LOAN_TYPE_OPTIONS_WITH_NONQM,
  ARM_TERM_OPTIONS,
  LOAN_TERM_OPTIONS,
  NONQM_PROGRAM_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  RESIDENCY_TYPE_OPTIONS,
  VA_FUNDING_FEE_OPTIONS
} from "../../../../../constants"
import { CreateSubscriptionPayload } from "../../../../../../types"

interface Props {
  values: CreateSubscriptionPayload
  setFormField: (name: string, value: string) => void
  hasDreamRate?: boolean
}

const ManageEngagementSelectors: FC<Props> = ({
  values,
  setFormField,
  hasDreamRate
}) => {
  const loanTypeOptions = hasDreamRate
    ? LOAN_TYPE_OPTIONS_WITH_NONQM
    : LOAN_TYPE_OPTIONS
  const shouldShowNonQmProgram = hasDreamRate && values.loan_type === "nonqm"

  const handleLoanTypeChange = (val: string) => {
    setFormField("loan_type", val)
    if (val !== "nonqm") {
      setFormField("nonqm_program", "")
    }
  }

  const { data: conformingLimitData } = useFetchConformingLimitQuery(
    {
      zipcode: values.zipcode
    },
    { skip: !values.zipcode }
  )

  const ltv = useMemo(() => {
    const numberLoanAmount = parseFloat(values.loan_amount)
    const numberListPrice = parseFloat(values.list_price)

    if (!numberLoanAmount || !numberListPrice) return ""

    const total =
      parseFloat(values.cash_out || "0") + parseFloat(values.loan_amount)
    const percent = Math.trunc((total / parseFloat(values.list_price)) * 100)

    return `LTV: ${percent}%`
  }, [values.cash_out, values.list_price, values.loan_amount])

  return (
    <div className="pt-12">
      <div className="text-xl font-bold">Loan scenario</div>
      <div className="pt-6 flex" role="group">
        <label key="purchase" className="cursor-pointer flex items-center">
          <Field
            type="radio"
            name="loan_purpose"
            value="purchase"
            className="form-radio h-4 w-4 text-primary-400 transition duration-150 ease-in-out"
          />
          <span className="ml-4">Purchase</span>
        </label>
        <label
          key="refinance"
          className="cursor-pointer flex items-center ml-8"
        >
          <Field
            type="radio"
            name="loan_purpose"
            value="refinance"
            className="form-radio h-4 w-4 text-primary-400 transition duration-150 ease-in-out"
          />
          <span className="ml-4">Refinance</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-8 pt-8">
        <div>
          <Form.Select
            label="Loan type"
            name="loan_type"
            selected={values.loan_type}
            data={loanTypeOptions}
            setSelected={handleLoanTypeChange}
          />
        </div>

        <div>
          {values.loan_type === "ARM" ? (
            <Form.Select
              label="Arm term"
              name="arm_term"
              selected={values.arm_term}
              data={ARM_TERM_OPTIONS}
              setSelected={val => setFormField("arm_term", val)}
            />
          ) : (
            <Form.Select
              label="Loan term"
              name="loan_term"
              selected={values.loan_term}
              data={LOAN_TERM_OPTIONS}
              setSelected={val => setFormField("loan_term", val)}
            />
          )}
        </div>
        {shouldShowNonQmProgram && (
          <div>
            <Form.Select
              label="Non-QM Program"
              name="nonqm_program"
              selected={values.nonqm_program ?? ""}
              data={NONQM_PROGRAM_OPTIONS}
              setSelected={val => setFormField("nonqm_program", val)}
            />
          </div>
        )}
        <div>
          <Form.Input
            name="loan_amount"
            label="Loan amount"
            placeholder="Ex. 700"
            required
          >
            <CurrencyInput setFormField={setFormField} />
          </Form.Input>
          {conformingLimitData?.conforming_limit && (
            <div className="text-neutral-400 text-sm pt-2">
              Conforming Limit:&nbsp; $
              {conformingLimitData?.conforming_limit.toLocaleString("en")}
            </div>
          )}
        </div>
        <div>
          <Form.Input
            name="credit_score"
            label="Credit score"
            placeholder="Ex. 700"
            type="number"
            required
          />
        </div>
        <div>
          <Form.Input
            name="list_price"
            label="Property value"
            placeholder="Ex. 700"
            helpText={ltv}
          >
            <CurrencyInput setFormField={setFormField} />
          </Form.Input>
        </div>
        <div>
          <Form.Select
            label="Property type"
            name="property_type"
            selected={values.property_type}
            data={PROPERTY_TYPE_OPTIONS}
            setSelected={val => setFormField("property_type", val)}
          />
        </div>
        <div>
          <Form.Select
            label="Property usage"
            name="residency_type"
            selected={values.residency_type}
            data={RESIDENCY_TYPE_OPTIONS}
            setSelected={val => setFormField("residency_type", val)}
          />
        </div>
        {values.loan_type === "va" && (
          <div>
            <Form.Select
              label="VA Funding Fee"
              name="va_funding_fee_type"
              selected={values.va_funding_fee_type}
              data={VA_FUNDING_FEE_OPTIONS}
              setSelected={val => setFormField("va_funding_fee_type", val)}
            />
          </div>
        )}

        {values.loan_purpose === "refinance" && (
          <div>
            <Form.Input name="cash_out" label="Cash out" placeholder="Ex. 700">
              <CurrencyInput setFormField={setFormField} />
            </Form.Input>
          </div>
        )}
        {hasDreamRate && <DreamRateField required />}
      </div>
    </div>
  )
}

export default ManageEngagementSelectors
